import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { fetchCompanies } from '@/features/companies/api'
import { createController, fetchController, updateController } from '@/features/controllers/api'
import { APP_TYPES } from '@/features/controllers/types'
import MicrogridSection from '@/features/microgrids/MicrogridSection'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'

/** Ports includes/controller_add.html (create) and includes/controller_details.html
 * (edit — a plain profile-edit form despite the filename). Both submit to
 * the canonical controllers_blueprint routes.
 *
 * The Company Detail page's "Add controller" section only ever let an
 * admin attach a controller that already exists - legacy's own
 * company_details.html had a "Create New" link right next to that
 * dropdown (pointing at this exact create form) for the case where the
 * controller doesn't exist yet, which this port had dropped. Restored via
 * an optional `?company_id=` query param this form pre-fills the Company
 * field from on create (edit mode ignores it - the fetched controller's
 * own company_id already wins), and on successful creation this way,
 * returns the admin to that company's page - where the just-created
 * controller now already appears, since it was created with that company
 * pre-selected - instead of the controller's own edit page. */
export default function ControllerFormPage() {
  const { controllerId } = useParams<{ controllerId?: string }>()
  const [searchParams] = useSearchParams()
  const prefillCompanyId = searchParams.get('company_id') ?? ''
  const isEdit = Boolean(controllerId)
  const navigate = useNavigate()

  const companiesQuery = useQuery({ queryKey: ['companies'], queryFn: fetchCompanies })
  const controllerQuery = useQuery({
    queryKey: ['controller', controllerId],
    queryFn: () => fetchController(controllerId!),
    enabled: isEdit,
  })

  const [controllerIdField, setControllerIdField] = useState('')
  const [companyId, setCompanyId] = useState(prefillCompanyId)
  const [appType, setAppType] = useState('')
  const [controllerName, setControllerName] = useState('')
  const [env, setEnv] = useState<'prod' | 'dev'>('prod')
  const [capacity, setCapacity] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!controllerQuery.data) return
    setControllerIdField(controllerQuery.data.controller_id)
    setCompanyId(controllerQuery.data.company_id ?? '')
    setAppType(controllerQuery.data.app_type ?? '')
    setControllerName(controllerQuery.data.controller_name ?? '')
    setEnv((controllerQuery.data.env as 'prod' | 'dev') ?? 'prod')
    setCapacity(controllerQuery.data.capacity ?? '')
  }, [controllerQuery.data])

  const createMutation = useMutation({
    mutationFn: () =>
      createController({ controller_id: controllerIdField, company_id: companyId, app_type: appType, controller_name: controllerName, env, capacity }),
    onSuccess: (created) =>
      navigate(prefillCompanyId ? `/companies/${prefillCompanyId}` : `/controllers/${created.controller_id}/edit`, { replace: true }),
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to create controller.'),
  })

  const updateMutation = useMutation({
    mutationFn: () => updateController(controllerId!, { company_id: companyId, app_type: appType, controller_name: controllerName, env, capacity }),
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to save controller.'),
  })

  if (isEdit && controllerQuery.isLoading) return <LoadingState label="Loading controller…" />
  if (isEdit && controllerQuery.isError) {
    return (
      <ErrorState
        message={controllerQuery.error instanceof Error ? controllerQuery.error.message : 'Failed to load.'}
        onRetry={() => controllerQuery.refetch()}
      />
    )
  }

  return (
    <div className="max-w-lg space-y-4">
      <h1 className="text-xl font-semibold text-text-primary">{isEdit ? 'Edit Controller' : 'New Controller'}</h1>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          setError(null)
          if (isEdit) updateMutation.mutate()
          else createMutation.mutate()
        }}
        className="space-y-3 rounded border border-border bg-card p-4"
      >
        <Field label="Controller Name" value={controllerName} onChange={setControllerName} required />
        <Field
          label="Controller ID"
          value={controllerIdField}
          onChange={setControllerIdField}
          required
          disabled={isEdit}
        />

        <div>
          <label className="mb-1 block text-xs text-text-secondary">Company</label>
          <select
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            required
            className="w-full rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary"
          >
            <option value="">Select…</option>
            {(companiesQuery.data ?? []).map((c) => (
              <option key={c.company_id} value={c.company_id}>
                {c.company_name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs text-text-secondary">App Type</label>
          <select
            value={appType}
            onChange={(e) => setAppType(e.target.value)}
            required
            className="w-full rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary"
          >
            <option value="">Select…</option>
            {APP_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs text-text-secondary">Environment</label>
          <select
            value={env}
            onChange={(e) => setEnv(e.target.value as 'prod' | 'dev')}
            className="w-full rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary"
          >
            <option value="prod">prod</option>
            <option value="dev">dev</option>
          </select>
        </div>

        <Field label="Capacity" value={capacity} onChange={setCapacity} required />

        {error && <p className="text-sm text-error">{error}</p>}

        <button
          type="submit"
          disabled={createMutation.isPending || updateMutation.isPending}
          className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {createMutation.isPending || updateMutation.isPending ? 'Saving…' : 'Save'}
        </button>
        {updateMutation.isSuccess && <span className="ml-3 text-sm text-brand">Saved.</span>}
      </form>

      {/* Microgrid linking only makes sense for a controller that already
          exists (POST /microgrids requires a real controller_id row) --
          create mode has no controller_id yet. */}
      {isEdit && controllerId && <MicrogridSection controllerId={controllerId} />}
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
  required,
  disabled,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  required?: boolean
  disabled?: boolean
}) {
  return (
    <div>
      <label className="mb-1 block text-xs text-text-secondary">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        className="w-full rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary disabled:opacity-60"
      />
    </div>
  )
}
